import { Link } from "react-router-dom";
import {
  ArrowRight,
  Clock,
  Wifi,
  Users,
  MapPin,
  Layers,
  Sparkles,
  Star,
  ChevronRight,
} from "lucide-react";
import { LOCATIONS, TESTIMONIALS, WHY_CHOOSE_US } from "@/data/siteData";
import { CTABanner } from "@/components/CTABanner";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from "@/components/ui/carousel";

const ICON_MAP = { Clock, Wifi, Users, MapPin, Layers, Sparkles };

const FeaturedLocations = () => {
  const featured = LOCATIONS.slice(0, 3);
  return (
    <section data-testid="featured-locations" className="py-12 sm:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-16">
          <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3">
            Our Spaces
          </p>
          <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
            Featured Locations
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 stagger-children">
          {featured.map((loc) => (
            <Link
              key={loc.id}
              to={`/locations/${loc.id}`}
              data-testid={`featured-card-${loc.id}`}
              className="block bg-white rounded-2xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] group"
            >
              <div className="aspect-[4/3] w-full overflow-hidden">
                <img
                  src={loc.image}
                  alt={loc.name}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-2">
                  <MapPin className="w-4 h-4 text-orange-500" />
                  <span className="text-xs uppercase tracking-[0.15em] font-medium text-slate-500">
                    {loc.tagline}
                  </span>
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">
                  {loc.name}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-4">
                  {loc.shortDescription}
                </p>
                <span
                  data-testid={`featured-view-${loc.id}`}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-orange-500 group-hover:text-orange-600 transition-colors"
                >
                  View Details
                  <ChevronRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </div>
            </Link>
          ))}
        </div>
        <div className="text-center mt-12">
          <Link
            to="/locations"
            data-testid="view-all-locations-btn"
            className="inline-flex items-center gap-2 rounded-full bg-white text-sky-600 font-medium px-6 py-3 hover:bg-slate-50 border border-slate-200 active:scale-95 transition-all shadow-sm"
          >
            View All Locations
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

const WhyChooseUs = () => (
  <section data-testid="why-choose-us" className="py-12 sm:py-24 bg-slate-50">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 sm:mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-sky-600 mb-3">
          Why Coffice
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
          Why Choose Us
        </h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 stagger-children">
        {WHY_CHOOSE_US.map((item) => {
          const Icon = ICON_MAP[item.icon];
          return (
            <div
              key={item.title}
              data-testid={`benefit-${item.icon.toLowerCase()}`}
              className="bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_4px_20px_rgb(0,0,0,0.03)] hover:shadow-[0_12px_30px_rgb(0,0,0,0.06)] transition-all duration-300"
            >
              <div className="w-12 h-12 rounded-xl bg-orange-50 flex items-center justify-center mb-5">
                <Icon className="w-6 h-6 text-orange-500" />
              </div>
              <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-slate-600 leading-relaxed">
                {item.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  </section>
);

const Testimonials = () => (
  <section data-testid="testimonials" className="py-12 sm:py-24 bg-white">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="text-center mb-10 sm:mb-16">
        <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-3">
          Testimonials
        </p>
        <h2 className="font-heading text-3xl sm:text-4xl font-semibold text-slate-900 tracking-tight">
          What Our Members Say
        </h2>
      </div>
      <Carousel opts={{ align: "start" }} data-testid="testimonials-carousel">
        <CarouselContent className="-ml-8 py-1">
          {TESTIMONIALS.map((t) => (
            <CarouselItem
              key={t.id}
              className="pl-8 sm:basis-1/2 lg:basis-1/3"
            >
              <div
                data-testid={`testimonial-${t.id}`}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100 flex flex-col h-full"
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-sm text-slate-600 leading-relaxed mb-6 italic flex-1">
                  "{t.text}"
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-sky-600 flex items-center justify-center text-white text-sm font-semibold flex-shrink-0">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>
    </div>
  </section>
);

export default function HomePage() {
  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section data-testid="hero-section" className="py-12 sm:py-24 lg:py-32 bg-slate-50 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-orange-100 rounded-full opacity-40 blur-3xl" />
          <div className="absolute bottom-10 left-10 w-56 h-56 bg-sky-100 rounded-full opacity-40 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <p className="text-xs uppercase tracking-[0.2em] font-medium text-orange-500 mb-4">
                Coworking Redefined
              </p>
              <h1
                data-testid="hero-heading"
                className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-slate-900 tracking-tight leading-none mb-6"
              >
                Work Anytime.{" "}
                <span className="text-orange-500">Grow Anytime.</span>
              </h1>
              <p className="text-base sm:text-lg text-slate-600 leading-relaxed mb-8 max-w-lg font-body">
                5+ coworking centers across Pune with 24x7 access,
                high-speed internet, meeting rooms, and private cabins.
                Your workspace, your rules.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  data-testid="hero-book-visit-btn"
                  className="rounded-full bg-orange-500 text-white font-medium px-6 py-3 hover:bg-orange-600 active:scale-95 transition-all shadow-md shadow-orange-500/20 inline-flex items-center gap-2"
                >
                  Book a Visit
                  <ArrowRight className="w-4 h-4" />
                </Link>
                <Link
                  to="/locations"
                  data-testid="hero-explore-btn"
                  className="rounded-full bg-white text-sky-600 font-medium px-6 py-3 hover:bg-slate-50 border border-slate-200 active:scale-95 transition-all shadow-sm"
                >
                  Explore Spaces
                </Link>
              </div>
              {/* Stats row */}
              <div className="flex gap-8 mt-10">
                <div>
                  <p className="font-heading text-2xl font-bold text-slate-900">5+</p>
                  <p className="text-xs text-slate-500 mt-0.5">Centers</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-slate-900">500+</p>
                  <p className="text-xs text-slate-500 mt-0.5">Members</p>
                </div>
                <div>
                  <p className="font-heading text-2xl font-bold text-slate-900">24/7</p>
                  <p className="text-xs text-slate-500 mt-0.5">Access</p>
                </div>
              </div>
            </div>
            {/* Hero images bento */}
            <div className="hidden lg:grid grid-cols-2 gap-4 animate-fade-in-up animation-delay-200">
              <div className="space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1626187890723-04ce3dbdc42a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBjb3dvcmtpbmclMjBzcGFjZSUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NDc1NTh8MA&ixlib=rb-4.1.0&q=85"
                    alt="Modern coworking space"
                    className="w-full h-48 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1626187853752-898f59dc180e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBjb3dvcmtpbmclMjBzcGFjZSUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NDc1NTh8MA&ixlib=rb-4.1.0&q=85"
                    alt="Coworking desks"
                    className="w-full h-64 object-cover"
                  />
                </div>
              </div>
              <div className="pt-8 space-y-4">
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1727857934741-93f20b9ffe71?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwxfHxwZW9wbGUlMjB3b3JraW5nJTIwc3RhcnR1cCUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NDc1NTh8MA&ixlib=rb-4.1.0&q=85"
                    alt="People working"
                    className="w-full h-64 object-cover"
                  />
                </div>
                <div className="rounded-2xl overflow-hidden shadow-lg">
                  <img
                    src="https://images.unsplash.com/photo-1703355685952-03ed19f70f51?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBtZWV0aW5nJTIwcm9vbSUyMG9mZmljZXxlbnwwfHx8fDE3NzY0NDc1NzZ8MA&ixlib=rb-4.1.0&q=85"
                    alt="Meeting room"
                    className="w-full h-48 object-cover"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <FeaturedLocations />
      <WhyChooseUs />
      <Testimonials />
      <CTABanner />
    </div>
  );
}
